import api from './api'
import type { ApiResponse, PaginatedResponse } from '../types'

export interface Campaign {
  id: number
  campaignUuid?: string
  title: string
  description?: string
  type: string
  status: string
  startDate?: string
  endDate?: string
  targetAmount?: number
  collectedAmount?: number
  targetParticipants?: number
  actualParticipants?: number
  location?: string
  coverImage?: string
  isFeatured?: boolean
  creator?: {
    id: number
    username: string
    firstName: string
    lastName: string
    profileImage?: string
  }
  createdAt?: string
  isParticipating?: boolean
}

export interface CampaignStats {
  activeCampaigns: number
  totalParticipants: number
  totalCollected: number
}

export interface CampaignCreateRequest {
  title: string
  description?: string
  type: string
  startDate?: string
  endDate?: string
  targetAmount?: number
  targetParticipants?: number
  location?: string
  coverImage?: string
  isFeatured?: boolean
}

const campaignService = {
  // Get all campaigns with optional filters
  async getCampaigns(params: {
    type?: string
    status?: string
    search?: string
    page?: number
    size?: number
  }): Promise<PaginatedResponse<Campaign>> {
    const queryParams = new URLSearchParams()
    if (params.type) queryParams.append('type', params.type)
    if (params.status) queryParams.append('status', params.status)
    if (params.search) queryParams.append('search', params.search)
    if (params.page !== undefined) queryParams.append('page', params.page.toString())
    if (params.size !== undefined) queryParams.append('size', params.size.toString())

    const response = await api.get<ApiResponse<PaginatedResponse<Campaign>>>(`/campaigns?${queryParams}`)
    return response.data.data
  },

  // Get campaign by ID
  async getCampaignById(id: number): Promise<Campaign> {
    const response = await api.get<ApiResponse<Campaign>>(`/campaigns/${id}`)
    return response.data.data
  },

  // Get featured campaigns
  async getFeaturedCampaigns(): Promise<Campaign[]> {
    const response = await api.get<ApiResponse<Campaign[]>>('/campaigns/featured')
    return response.data.data
  },

  // Get campaign statistics
  async getStats(): Promise<CampaignStats> {
    const response = await api.get<ApiResponse<CampaignStats>>('/campaigns/stats')
    return response.data.data
  },

  // Create a new campaign
  async createCampaign(data: CampaignCreateRequest): Promise<Campaign> {
    const response = await api.post<ApiResponse<Campaign>>('/campaigns', data)
    return response.data.data
  },

  // Get current user's own campaigns (all statuses, incl. DRAFT)
  async getMyCampaigns(params?: { page?: number; size?: number }): Promise<PaginatedResponse<Campaign>> {
    const queryParams = new URLSearchParams()
    if (params?.page !== undefined) queryParams.append('page', params.page.toString())
    if (params?.size !== undefined) queryParams.append('size', params.size.toString())
    const qs = queryParams.toString()
    const response = await api.get<ApiResponse<PaginatedResponse<Campaign>>>(`/campaigns/my${qs ? `?${qs}` : ''}`)
    return response.data.data
  },

  // Publish/activate a campaign the current user created (DRAFT -> ACTIVE)
  async publishCampaign(id: number): Promise<Campaign> {
    const response = await api.post<ApiResponse<Campaign>>(`/campaigns/${id}/publish`)
    return response.data.data
  },

  // Update a campaign the current user created (creator only)
  async updateCampaign(id: number, data: CampaignCreateRequest): Promise<Campaign> {
    const response = await api.put<ApiResponse<Campaign>>(`/campaigns/${id}`, data)
    return response.data.data
  },

  // Delete a campaign the current user created (creator only)
  async deleteCampaign(id: number): Promise<void> {
    await api.delete<ApiResponse<void>>(`/campaigns/${id}`)
  },

  // Join a campaign
  async participate(campaignId: number): Promise<Campaign> {
    const response = await api.post<ApiResponse<Campaign>>(`/campaigns/${campaignId}/participate`)
    return response.data.data
  },

  // Leave a campaign
  async leaveCampaign(campaignId: number): Promise<Campaign> {
    const response = await api.delete<ApiResponse<Campaign>>(`/campaigns/${campaignId}/participate`)
    return response.data.data
  }
}

export default campaignService